import { useMemo } from "react";
import { SelectOption } from "../../../../../../types/select";
import { findSelectOption } from "../../../../../libs/utils";
import { type BreakdownMode, resolveBreakdownMode } from "../../../../BookingForm/components/Commercial/add-plan";
import { type BookingSelectorState, useBookingSelector } from "../../../../BookingForm/hooks/useBookingSelector";
import { useTorRules } from "../../../../BookingForm/hooks/useTorRules";

export const periodOptions: SelectOption[] = [
  { label: "Years", value: "years" },
  { label: "Months", value: "months" },
  { label: "Days", value: "days" },
];

export type RenewalTermsFormValues = {
  renewFor?: number | string;
  period?: SelectOption;
  torRuleType?: SelectOption;
  breakdownMode?: BreakdownMode;
  renewalAmount?: number | string;
  isTopUpRequired?: boolean;
  securityDepositTopUp?: {
    currentAmount?: number | string;
    requiredAmount?: number | string;
    requiredPercentage?: number | string;
  };
};

const getRenewalTermsFormValues = (
  bookingDetails: BookingSelectorState,
  torRulesOptions: SelectOption[],
): RenewalTermsFormValues => {
  const { bookingTerms, totalSecurityDepositPaid, torType, generate } = bookingDetails;
  const securityDepositTopUp = bookingTerms?.securityDepositTopUp;

  return {
    renewFor: bookingTerms?.numberOfRenewalTerms ?? 1,
    period: findSelectOption(periodOptions, bookingTerms?.durationType) ?? periodOptions[0],
    torRuleType: torRulesOptions.find((option) => option.value === torType) ?? torRulesOptions[0],
    breakdownMode: resolveBreakdownMode(generate),
    renewalAmount: bookingTerms?.renewalAmount ?? bookingTerms?.annualBaseRent,
    isTopUpRequired: securityDepositTopUp != null,
    securityDepositTopUp: {
      currentAmount: totalSecurityDepositPaid ?? securityDepositTopUp?.currentAmount ?? 0,
      requiredAmount: securityDepositTopUp?.requiredAmount ?? 0,
      requiredPercentage: securityDepositTopUp?.requiredPercentage ?? 0,
    },
  };
};

export const useRenewalTermsForm = () => {
  const bookingDetails = useBookingSelector();
  const { torRulesOptions } = useTorRules();

  const formValues = useMemo(
    () => getRenewalTermsFormValues(bookingDetails, torRulesOptions),
    [bookingDetails, torRulesOptions],
  );

  return {
    formValues,
    leaseId: bookingDetails?.leaseId,
    leaseEndDate: bookingDetails?.leaseTerms?.leaseEndDate,
    torRulesOptions,
    periodOptions,
  };
};
